import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  Bell,
  CarFront,
  FileText,
  Globe,
  Paperclip,
  Shield,
} from 'lucide-react';
import { SeoHead } from '../common/SeoHead';
import { JsonLd } from '../common/JsonLd';

export const Landing = () => {
  const { t, i18n } = useTranslation();

  const toggleLanguage = () => {
    const next = i18n.language?.startsWith('uk') ? 'en' : 'uk';
    i18n.changeLanguage(next);
  };

  const features = [
    {
      icon: CarFront,
      title: t('landing.features.vehicles.title'),
      text: t('landing.features.vehicles.text'),
    },
    {
      icon: FileText,
      title: t('landing.features.records.title'),
      text: t('landing.features.records.text'),
    },
    {
      icon: Paperclip,
      title: t('landing.features.files.title'),
      text: t('landing.features.files.text'),
    },
    {
      icon: Bell,
      title: t('landing.features.reminders.title'),
      text: t('landing.features.reminders.text'),
    },
    {
      icon: BarChart3,
      title: t('landing.features.analytics.title'),
      text: t('landing.features.analytics.text'),
    },
    {
      icon: Shield,
      title: t('landing.features.privacy.title'),
      text: t('landing.features.privacy.text'),
    },
  ];

  const faqItems = [1, 2, 3, 4].map((n) => ({
    question: t(`landing.faq.q${n}.question`),
    answer: t(`landing.faq.q${n}.answer`),
  }));

  const appJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: t('common.appName'),
    alternateName: 'Vehicle Logbook',
    url: 'https://vehicle-logbook.top/',
    applicationCategory: 'UtilitiesApplication',
    operatingSystem: 'Web',
    browserRequirements: 'Requires JavaScript',
    inLanguage: ['uk', 'en'],
    description: t('seo.home.description'),
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'UAH',
      description: t('landing.supportNote'),
    },
    featureList: features.map((f) => f.title).join(', '),
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <SeoHead page="home" />
      <JsonLd data={appJsonLd} id="jsonld-app" />
      <JsonLd data={faqJsonLd} id="jsonld-faq" />

      <div className="min-h-screen bg-gray-50">
        <header className="border-b border-gray-200 bg-white">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <img
                src="/favicon.png"
                alt={t('common.appName')}
                className="h-9 w-9 rounded-lg"
                width={36}
                height={36}
              />
              <span className="text-lg font-semibold text-gray-900">
                {t('common.appName')}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={toggleLanguage}
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"
                aria-label={t('landing.switchLanguage')}
              >
                <Globe className="h-4 w-4" />
                {i18n.language?.startsWith('uk') ? 'EN' : 'UA'}
              </button>
              <Link
                to="/login"
                className="rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                {t('auth.login')}
              </Link>
              <Link
                to="/register"
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
              >
                {t('landing.ctaPrimary')}
              </Link>
            </div>
          </div>
        </header>

        <main>
          <section className="bg-gradient-to-br from-primary-700 via-primary-600 to-primary-900 text-white">
            <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
              <div className="max-w-3xl">
                <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
                  {t('landing.hero.title')}
                </h1>
                <p className="mt-6 text-lg leading-relaxed text-primary-100">
                  {t('landing.hero.subtitle')}
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link
                    to="/register"
                    className="inline-flex items-center rounded-lg bg-white px-6 py-3 text-base font-semibold text-primary-700 shadow-sm hover:bg-primary-50"
                  >
                    {t('landing.ctaPrimary')}
                  </Link>
                  <Link
                    to="/login"
                    className="inline-flex items-center rounded-lg bg-primary-500/30 px-6 py-3 text-base font-semibold text-white ring-1 ring-white/30 hover:bg-primary-500/40"
                  >
                    {t('landing.ctaSecondary')}
                  </Link>
                </div>
                <p className="mt-6 text-sm text-primary-200">
                  {t('landing.supportNote')}{' '}
                  <Link
                    to="/about"
                    className="font-medium text-white underline underline-offset-2 hover:text-primary-50"
                  >
                    {t('landing.supportLink')}
                  </Link>
                </p>
              </div>
            </div>
          </section>

          <section
            className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8"
            aria-labelledby="features-heading"
          >
            <h2
              id="features-heading"
              className="text-center text-3xl font-bold text-gray-900"
            >
              {t('landing.features.title')}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-gray-600">
              {t('landing.features.subtitle')}
            </p>
            <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <article
                    key={feature.title}
                    className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <h3 className="mt-4 text-lg font-semibold text-gray-900">
                      {feature.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-gray-600">
                      {feature.text}
                    </p>
                  </article>
                );
              })}
            </div>
          </section>

          <section
            className="border-t border-gray-200 bg-white"
            aria-labelledby="faq-heading"
          >
            <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
              <h2
                id="faq-heading"
                className="text-center text-3xl font-bold text-gray-900"
              >
                {t('landing.faq.title')}
              </h2>
              <dl className="mt-10 space-y-8">
                {faqItems.map((item) => (
                  <div key={item.question}>
                    <dt className="text-lg font-semibold text-gray-900">
                      {item.question}
                    </dt>
                    <dd className="mt-2 text-gray-600 leading-relaxed">
                      {item.answer}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>

          <section className="bg-primary-50">
            <div className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6 lg:px-8">
              <h2 className="text-2xl font-bold text-gray-900">
                {t('landing.bottomCta.title')}
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-gray-600">
                {t('landing.bottomCta.subtitle')}
              </p>
              <Link
                to="/register"
                className="mt-8 inline-flex items-center rounded-lg bg-primary-600 px-8 py-3 text-base font-semibold text-white shadow-sm hover:bg-primary-700"
              >
                {t('landing.ctaPrimary')}
              </Link>
            </div>
          </section>
        </main>

        <footer className="border-t border-gray-200 bg-white">
          <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 py-8 sm:flex-row sm:px-6 lg:px-8">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} {t('common.appName')}
            </p>
            <nav className="flex gap-6 text-sm">
              <Link to="/about" className="text-gray-600 hover:text-gray-900">
                {t('auth.brand.aboutLink')}
              </Link>
              <Link to="/login" className="text-gray-600 hover:text-gray-900">
                {t('auth.login')}
              </Link>
              <Link
                to="/register"
                className="text-gray-600 hover:text-gray-900"
              >
                {t('auth.register')}
              </Link>
            </nav>
          </div>
        </footer>
      </div>
    </>
  );
};
